/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * In-memory Cookie Store API backing storage and CDU consent detection, used by
 * the Cookie Store wrapper when consent is not yet accepted.
 */

/**
 * A stored cookie record held in the in-memory Cookie Store.
 */
type StoredCookie = { value: string; expires?: number; path?: string; domain?: string; sameSite?: string };

/**
 * Determines whether a stored cookie record has expired relative to `now`.
 *
 * @param stored The stored cookie record.
 * @param now The reference timestamp in milliseconds.
 * @returns True when the record carries an expiry earlier than `now`.
 */
function isExpired(stored: StoredCookie, now: number): boolean {
  return Boolean(stored.expires && stored.expires < now);
}

/**
 * Builds the public Cookie Store record shape from a stored entry.
 *
 * @param name The cookie name.
 * @param stored The stored cookie record.
 * @returns The Cookie Store API record with defaults applied.
 */
function toCookieRecord(name: string, stored: StoredCookie): any {
  return {
    name,
    value: stored.value,
    domain: stored.domain || null,
    path: stored.path || '/',
    expires: stored.expires ? stored.expires : null,
    secure: false,
    sameSite: stored.sameSite || 'lax',
  };
}

/**
 * In-memory storage for Cookie Store API when consent is not accepted.
 */
export class InMemoryCookieStore {
  public data: Map<string, StoredCookie> = new Map();

  async get(name: string): Promise<any | null> {
    const stored = this.data.get(name);
    if (!stored) {
      return null;
    }
    if (isExpired(stored, Date.now())) {
      this.data.delete(name);
      return null;
    }
    return toCookieRecord(name, stored);
  }

  async getAll(options?: { name?: string; url?: string }): Promise<any[]> {
    const now = Date.now();
    this.pruneExpired(now);
    return Array.from(this.data.entries())
      .filter(([name]) => !options?.name || name === options.name)
      .map(([name, data]) => toCookieRecord(name, data));
  }

  /**
   * Removes every expired cookie from the store relative to `now`.
   *
   * @param now The reference timestamp in milliseconds.
   */
  private pruneExpired(now: number): void {
    for (const [name, data] of this.data.entries()) {
      if (isExpired(data, now)) {
        this.data.delete(name);
      }
    }
  }

  async set(name: string, value: string, options?: any): Promise<void> {
    this.data.set(name, {
      value,
      expires: options?.expires ? new Date(options.expires).getTime() : undefined,
      path: options?.path,
      domain: options?.domain,
      sameSite: options?.sameSite,
    });
  }

  async delete(name: string): Promise<void> {
    this.data.delete(name);
  }

  clear(): void {
    this.data.clear();
  }

  getContents(): Record<string, string> {
    const contents: Record<string, string> = {};
    this.data.forEach((data, name) => {
      contents[name] = data.value;
    });
    return contents;
  }
}

let inMemoryCookieStore: InMemoryCookieStore | null = null;

/**
 * Returns the process-wide in-memory Cookie Store, creating it on first use.
 *
 * @returns The singleton {@link InMemoryCookieStore} instance.
 */
export function getInMemoryCookieStore(): InMemoryCookieStore {
  if (!inMemoryCookieStore) {
    inMemoryCookieStore = new InMemoryCookieStore();
  }
  return inMemoryCookieStore;
}

/**
 * Reads the consent status held in the in-memory cookie store, if present.
 *
 * @returns The parsed status, or null when no in-memory consent cookie exists.
 */
function readMemoryConsent(): string | null {
  const memoryCookie = getInMemoryCookieStore().data.get('cdu_consent_v1');
  if (!memoryCookie) {
    return null;
  }
  return JSON.parse(memoryCookie.value).status || 'pending';
}

/**
 * Reads the consent status from the real `document.cookie`, if present.
 *
 * @returns The parsed status, or null when no document consent cookie exists.
 */
function readDocumentConsent(): string | null {
  const consentCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.trim().startsWith('cdu_consent_v1='));
  if (!consentCookie) {
    return null;
  }
  const decodedValue = decodeURIComponent(consentCookie.split('=')[1]);
  return JSON.parse(decodedValue).status || 'pending';
}

/**
 * Gets consent status from cookies or in-memory storage.
 */
function getConsentStatus(): 'pending' | 'accepted' | 'rejected' {
  try {
    const status = readMemoryConsent() ?? readDocumentConsent();
    if (status) {
      return status as 'pending' | 'accepted' | 'rejected';
    }
  } catch (error) {
    // If parsing fails, assume pending
  }
  return 'pending';
}

/**
 * Checks if consent is accepted.
 */
export function hasConsent(): boolean {
  return getConsentStatus() === 'accepted';
}
